import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '../utils/trpc'

// Users
export const useUsersQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.users.getUsers.queryOptions())
}

export const useFindUserFromId = () => {
    const usersQuery = useUsersQuery()
    return (userId: string) => {
        const users = usersQuery.data || []
        return users.find(user => user.userId == userId)
    }
}

export const useUserName = () => {
    const findUser = useFindUserFromId()
    return (userId: string, fallback = 'loading...') => {
        const user = findUser(userId)
        return user ? `${user.firstName} ${user.lastName}` : fallback
    }
}

// Groups
export const useGroupsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.groups.getGroups.queryOptions())
}

// Conjunctional Agencies
export const useConjunctionalAgenciesQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.conjunctionalAgencies.getConjunctionalAgencies.queryOptions())
}

// Saved Filters
export const useSavedFiltersQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.savedFilters.getSavedFilters.queryOptions())
}

// Automations
export const useAutomationsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.automation.getAutomations.queryOptions())
}

export const useAutomationSettingsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.automation.getAutomationSettings.queryOptions())
}

// Chat
export const useChatGroupsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.chat.getChatGroups.queryOptions())
}

export const useChatConfigQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.chat.getChatConfig.queryOptions())
}

export const useChatApi = (chatGroupId: string) => {
    const trpc = useTRPC()
    const query = useQuery(trpc.chat.searchChat.queryOptions({ chatGroupId }))
    return {
        ...query,
        data: query.data?.items,
        refresh: query.refetch,
    }
}

// Todos
export const useTodoQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.todos.getTodos.queryOptions())
}

export const useSharedTodosQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.todos.getAllSharedTodos.queryOptions())
}

// Org
export const useOrgDetailsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.org.getOrgDetails.queryOptions())
}

export const useOrgIntegrationsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.org.getOrgIntegrations.queryOptions())
}

// Roles
export const useRolesQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.roles.getRoles.queryOptions())
}

// Notifications
export const useNotificationsQuery = (params?: { Limit?: number; ExclusiveStartKey?: any }) => {
    const trpc = useTRPC()
    return useQuery(
        trpc.notifications.getNotifications.queryOptions({
            Limit: params?.Limit || 20,
            ExclusiveStartKey: params?.ExclusiveStartKey,
        }),
    )
}

// Xero
export const useXeroAccountsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.xero.getAccounts.queryOptions())
}

export const useXeroItemsQuery = () => {
    const trpc = useTRPC()
    return useQuery(trpc.xero.getItems.queryOptions())
}
